/**
 * Обработка звука трансляции: вычитаем из захваченного звука системы то, что играет сам Кличат.
 * Реализует TrackProcessor LiveKit — так же, как обработка микрофона: дорожка проходит через
 * AudioWorklet (stripWorklet.js), в SFU уходит очищенная.
 *
 * Второй вход worklet'а — узел с нашим собственным микшем (см. store/voice.ts): опора должна быть
 * ровно тем, что слышит показывающий, иначе вычитать нечего.
 */
import type { AudioProcessorOptions, TrackProcessor } from 'livekit-client'
import { Track } from 'livekit-client'

export interface StripState {
  /** Вычитание включилось: задержка найдена и польза подтвердилась. */
  active: boolean
  /** Насколько тише стал наш звук в захвате, дБ. */
  erle: number
  /** Найденная задержка между нашим выходом и захватом, мс. */
  delayMs: number
}

export class StreamStripProcessor implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  name = 'klichat-strip'
  processedTrack?: MediaStreamTrack
  onState: ((state: StripState) => void) | null = null

  private reference: AudioNode
  private enabled: boolean
  private source: MediaStreamAudioSourceNode | null = null
  private node: AudioWorkletNode | null = null
  private dest: MediaStreamAudioDestinationNode | null = null

  constructor(reference: AudioNode, enabled = true) {
    this.reference = reference
    this.enabled = enabled
  }

  async init(opts: AudioProcessorOptions): Promise<void> {
    if (opts.kind !== Track.Kind.Audio) throw new Error('klichat-strip: только аудио')
    await this.destroy()
    const ctx = opts.audioContext ?? (this.reference.context as AudioContext)
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
    await ctx.audioWorklet.addModule(new URL('./stripWorklet.js', import.meta.url))
    this.source = ctx.createMediaStreamSource(new MediaStream([opts.track]))
    this.node = new AudioWorkletNode(ctx, 'klichat-strip', {
      numberOfInputs: 2,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: { enabled: this.enabled },
    })
    this.node.port.onmessage = (e: MessageEvent<{ type?: string; active?: boolean; erle?: number; delayMs?: number }>) => {
      const m = e.data
      if (m.type === 'strip') this.onState?.({ active: Boolean(m.active), erle: m.erle ?? 0, delayMs: m.delayMs ?? 0 })
    }
    this.source.connect(this.node, 0, 0)
    this.reference.connect(this.node, 0, 1)
    this.dest = ctx.createMediaStreamDestination()
    this.node.connect(this.dest)
    const track = this.dest.stream.getAudioTracks()[0]
    if (!track) throw new Error('klichat-strip: нет выходной дорожки')
    this.processedTrack = track
  }

  async restart(opts: AudioProcessorOptions): Promise<void> {
    await this.init(opts)
  }

  async destroy(): Promise<void> {
    try {
      this.reference.disconnect(this.node as AudioNode)
    } catch {
      /* уже отключён */
    }
    this.source?.disconnect()
    this.node?.disconnect()
    this.node?.port.close()
    this.processedTrack?.stop()
    this.source = null
    this.node = null
    this.dest = null
    delete this.processedTrack
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    this.node?.port.postMessage({ type: 'config', enabled })
  }
}
